import { FC, useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';

export const LanguageSelector: FC<{ className?: string }> = ({ className = "" }) => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const languages = [
        { code: 'ru', label: 'Русский', flag: '🇷🇺' },
        { code: 'en', label: 'English', flag: '🇺🇸' },
        { code: 'pl', label: 'Polski', flag: '🇵🇱' },
        { code: 'uk', label: 'Українська', flag: '🇺🇦' }
    ];

    const currentLang = languages.find(l => l.code === (i18n.language?.split('-')[0])) || languages[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (code: string) => {
        i18n.changeLanguage(code);
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    // Escape closes the popup and returns focus to the trigger (standard popup a11y).
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape' && isOpen) {
            e.preventDefault();
            setIsOpen(false);
            triggerRef.current?.focus();
        }
    };

    return (
        <div className={`relative ${className}`} ref={containerRef} onKeyDown={handleKeyDown}>
            <button
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 dark:text-slate-400 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900"
                title={t('common.select_language', 'Select Language')}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <Languages size={18} className="group-hover:text-blue-600 transition-colors"/>
                <span className="text-xs font-bold uppercase tracking-tighter">{currentLang.code}</span>
            </button>

            {isOpen && (
                <div className="absolute top-full mt-2 right-0 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 motion-reduce:animate-none">
                    <div className="p-2 border-b border-slate-100 dark:border-slate-700">
                        <p className="text-micro text-slate-400 uppercase px-2 py-1">{t('common.select_language', 'Выберите язык')}</p>
                    </div>
                    <div className="p-1">
                        {languages.map((lang) => {
                            const isActive = i18n.language?.startsWith(lang.code);
                            return (
                                <button
                                    key={lang.code}
                                    onClick={() => handleSelect(lang.code)}
                                    aria-current={isActive ? 'true' : undefined}
                                    className={`
                                        w-full flex items-center justify-between p-2.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                                        ${isActive
                                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold'
                                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}
                                    `}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-lg">{lang.flag}</span>
                                        <span>{lang.label}</span>
                                    </div>
                                    {isActive && <Check size={16} />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
